export const ONBOARDING_STEP = {
  VALIDATING: "validating",
  BUSINESS_INFORMATION: "business-information",
  OWNER_INFORMATION: "owner-information",
  BANK_ACCOUNT: "bank-account",
  COMPLIANCE_POLICIES: "compliance-policies",
  REVIEW: "review",
  COMPLETE: "complete",
} as const;

export type OnboardingStepSlug = (typeof ONBOARDING_STEP)[keyof typeof ONBOARDING_STEP];

/** Ordered wizard steps (excludes validating and complete). */
export const ONBOARDING_WIZARD_STEPS: OnboardingStepSlug[] = [
  ONBOARDING_STEP.BUSINESS_INFORMATION,
  ONBOARDING_STEP.OWNER_INFORMATION,
  ONBOARDING_STEP.BANK_ACCOUNT,
  ONBOARDING_STEP.COMPLIANCE_POLICIES,
  ONBOARDING_STEP.REVIEW,
];

export const onboardingBase = (token: string) => `/onboarding/${token}`;

export const onboardingStepPath = (token: string, step: OnboardingStepSlug) =>
  `${onboardingBase(token)}/${step}`;

export const getStepFromPathname = (pathname: string): OnboardingStepSlug | null => {
  const segments = pathname.split("/").filter(Boolean);
  const stepSegment = segments[segments.length - 1];
  const values = Object.values(ONBOARDING_STEP) as string[];
  return values.includes(stepSegment) ? (stepSegment as OnboardingStepSlug) : null;
};

export const getNextWizardStep = (current: OnboardingStepSlug): OnboardingStepSlug | null => {
  const index = ONBOARDING_WIZARD_STEPS.indexOf(current);
  if (index < 0 || index >= ONBOARDING_WIZARD_STEPS.length - 1) {
    return null;
  }
  return ONBOARDING_WIZARD_STEPS[index + 1];
};

export const getPreviousWizardStep = (current: OnboardingStepSlug): OnboardingStepSlug | null => {
  const index = ONBOARDING_WIZARD_STEPS.indexOf(current);
  if (index <= 0) {
    return null;
  }
  return ONBOARDING_WIZARD_STEPS[index - 1];
};

export const isWizardStep = (step: OnboardingStepSlug | null | undefined): step is OnboardingStepSlug =>
  step != null && ONBOARDING_WIZARD_STEPS.includes(step);

/** Step to open after token validation (default: first wizard step). */
export const resolvePostValidationStep = (
  returnTo?: OnboardingStepSlug | null,
  lastVisitedStep?: OnboardingStepSlug | null,
): OnboardingStepSlug => {
  if (isWizardStep(returnTo)) {
    return returnTo;
  }
  if (isWizardStep(lastVisitedStep)) {
    return lastVisitedStep;
  }
  return ONBOARDING_STEP.BUSINESS_INFORMATION;
};
